/**
 * Round DMS-UAT-1 (2026) — Issue 9.
 *
 * Single source of truth for the "ECN Will Change" checklist. Previously the
 * list was duplicated in EcnDetailsTab.jsx and DmsViewModal.js with a typo
 * ("Use to Deplation") baked into stored values.
 *
 * Storage note (zero schema change): the checked options are persisted as a
 * comma-joined string in DmsEcr.ecrWillChange (modern convention since the
 * first DMS rounds). Options therefore must never contain a comma.
 *
 * Back-compat: documents saved before this round may carry the misspelled
 * "Use to Deplation" token. normalizeWillChangeValues() maps legacy aliases
 * to the canonical label on LOAD so the checkbox still renders checked; on
 * the next save the canonical spelling is written. Stored data is never
 * mutated except through a user-initiated save.
 */

export const ECN_WILL_CHANGE_OPTIONS = [
    "Acceptance Test Plan",
    "BOM/BOR",
    "C of C",
    "Customer Notification",
    "CNC Program",
    "Dispose/Purge Inventory",
    "Drawing",
    "EPICOR BOM",
    "EPICOR MOM",
    "EPICOR Part File",
    "FAIR",
    "Form Control Docs/Work",
    "Instructions",
    "Inspection Test Plan",
    "Label/Lasermark Program",
    "MAFIA",
    "MPC Plan",
    "PMA Approval",
    "PPAP",
    "QD Table",
    "Rework Inventory",
    "Supplier Notification",
    "Update Open Orders",
    "Use To Depletion",
    // Round DMS-UAT-1 — Issue 9: new option requested in UAT ("Under ECN
    // Details, add ECR/CR Change Request"). No schema change needed — it is
    // just another token in the existing comma-joined string.
    "ECR/CR Change Request",
    "Other",
];

// Legacy/alternate spellings → canonical option label. Keys are compared
// case-insensitively after trimming.
const LEGACY_ALIASES = {
    "use to deplation": "Use To Depletion",
    "use to depletion": "Use To Depletion",
    "acceptance test": "Acceptance Test Plan",
};

/**
 * Normalize a raw stored option token to its canonical label.
 * Unknown tokens (including "Other:<free text>") pass through unchanged so
 * nothing is ever lost.
 *
 * Round DMS-UAT-2 — defensively strips any HTML markup that legacy rows
 * carried inside ecrWillChange (the backend now returns plain text, but old
 * cached responses or other callers may still hand us markup).
 */
export function normalizeWillChangeValue(raw) {
    let trimmed = (raw || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!trimmed) return "";
    const alias = LEGACY_ALIASES[trimmed.toLowerCase()];
    if (alias) return alias;
    const exact = ECN_WILL_CHANGE_OPTIONS.find(
        (o) => o.toLowerCase() === trimmed.toLowerCase()
    );
    return exact || trimmed;
}

/**
 * Split a stored comma-joined ecrWillChange string into normalized tokens.
 */
export function normalizeWillChangeValues(stored) {
    if (!stored) return [];
    return String(stored)
        .split(",")
        .map(normalizeWillChangeValue)
        .filter(Boolean);
}

export default ECN_WILL_CHANGE_OPTIONS;
